// ====================================================
// VidyaTrack - Academic Calendar Page
// ====================================================

import React, { useState, useEffect } from "react";
import { FiPlus, FiChevronLeft, FiChevronRight, FiTrash2 } from "react-icons/fi";
import { useAuth } from "../contexts/AuthContext";
import { useData } from "../contexts/DataContext";
import Modal from "../components/shared/Modal";
import { toast } from "react-toastify";
import {


  format, startOfMonth, endOfMonth, eachDayOfInterval,
  isSameMonth, isSameDay, addMonths, subMonths, isToday,
  startOfWeek, endOfWeek, isPast
} from "date-fns";

// Safe date converter (handles Firestore Timestamps)
const toDate = v => { if(!v) return new Date(); if(typeof v?.toDate==="function") return v.toDate(); return new Date(v); };



const EVENT_TYPES = [
  { val: "exam", label: "Exam", color: "#ef4444" },
  { val: "holiday", label: "Holiday", color: "#8b5cf6" },
  { val: "event", label: "School Event", color: "#10b981" },
  { val: "meeting", label: "Meeting", color: "#f59e0b" },
];

export default function CalendarPage() {
  const { userData } = useAuth();
  const { getEvents, addEvent, deleteEvent } = useData();
  const role = userData?.role;
  const canManage = role === "admin";

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", date: "", type: "event", description: "" });
  const [errors, setErrors] = useState({});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadEvents(); }, []);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const data = await getEvents();
      setEvents(data);
    } catch { toast.error("Failed to load events"); }
    finally { setLoading(false); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.title.trim()) errs.title = "Title is required";
    if (!form.date) errs.date = "Date is required";
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      const typeInfo = EVENT_TYPES.find(t => t.val === form.type);
      await addEvent({ ...form, date: toDate(form.date).toISOString(), color: typeInfo?.color });
      toast.success("Event added!");
      setShowModal(false);
      setForm({ title: "", date: "", type: "event", description: "" });
      setErrors({});
      loadEvents();
    } catch { toast.error("Failed to add event"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try { await deleteEvent(id); toast.success("Event deleted!"); loadEvents(); }
    catch { toast.error("Failed to delete"); }
  };

  // Calendar grid
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calStart = startOfWeek(monthStart);
  const calEnd = endOfWeek(monthEnd);
  const calDays = eachDayOfInterval({ start: calStart, end: calEnd });

  const getEventsForDay = (day) =>
    events.filter(e => isSameDay(toDate(e.date), day));

  const getDayEvents = (day) => getEventsForDay(day);

  // Upcoming events
  const upcomingEvents = events
    .filter(e => !isPast(toDate(e.date)) || isSameDay(toDate(e.date), new Date()))
    .sort((a, b) => toDate(a.date) - toDate(b.date))
    .slice(0, 8);

  // Selected day events
  const selectedDayEvents = selectedDay ? getEventsForDay(selectedDay) : [];

  return (
    <div className="animate-fade">
      <div className="page-header">
        <div>
          <h1 className="page-title">📅 Academic Calendar</h1>
          <p className="page-subtitle">School events, holidays, and examinations</p>
        </div>
        {canManage && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <FiPlus size={16} /> Add Event
          </button>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 20, alignItems: "start" }}>
        {/* Calendar */}
        <div className="card">
          {/* Month navigation */}
          <div className="card-header">
            <button className="btn btn-ghost btn-icon" onClick={() => setCurrentDate(subMonths(currentDate, 1))}>
              <FiChevronLeft />
            </button>
            <h3 style={{ fontSize: "1.1rem", fontWeight: 700 }}>
              {format(currentDate, "MMMM yyyy")}
            </h3>
            <button className="btn btn-ghost btn-icon" onClick={() => setCurrentDate(addMonths(currentDate, 1))}>
              <FiChevronRight />
            </button>
          </div>
          <div className="card-body">
            {/* Day names */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4, marginBottom: 8 }}>
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
                <div key={d} style={{ textAlign: "center", fontSize: "0.72rem", fontWeight: 700, color: "var(--gray-500)", padding: "8px 4px" }}>{d}</div>
              ))}
            </div>

            {/* Days */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4 }}>
              {calDays.map(day => {
                const dayEvents = getDayEvents(day);
                const inMonth = isSameMonth(day, currentDate);
                const todayFlag = isToday(day);
                const selected = selectedDay && isSameDay(day, selectedDay);

                return (
                  <div
                    key={day.toISOString()}
                    onClick={() => setSelectedDay(isSameDay(day, selectedDay) ? null : day)}
                    style={{
                      minHeight: 60,
                      padding: "6px 4px",
                      borderRadius: "10px",
                      background: selected ? "var(--blue-100)" : todayFlag ? "var(--blue-700)" : "transparent",
                      border: `1px solid ${selected ? "var(--blue-700)" : todayFlag ? "var(--blue-700)" : "var(--border)"}`,
                      cursor: "pointer",
                      transition: "var(--t)",
                      opacity: inMonth ? 1 : 0.3
                    }}
                    onMouseEnter={e => { if (!selected && !todayFlag) e.currentTarget.style.background = "var(--border)"; }}
                    onMouseLeave={e => { if (!selected && !todayFlag) e.currentTarget.style.background = "transparent"; }}
                  >
                    <div style={{
                      textAlign: "center",
                      fontSize: "0.82rem",
                      fontWeight: todayFlag ? 700 : 400,
                      color: todayFlag ? "#fff" : selected ? "var(--blue-700)" : "var(--gray-900)",
                      marginBottom: 3
                    }}>
                      {format(day, "d")}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {dayEvents.slice(0, 2).map(evt => (
                        <div key={evt.id} style={{
                          fontSize: "0.6rem",
                          background: evt.color,
                          color: "#fff",
                          borderRadius: "3px",
                          padding: "1px 4px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }}>
                          {evt.title}
                        </div>
                      ))}
                      {dayEvents.length > 2 && (
                        <div style={{ fontSize: "0.6rem", color: "var(--gray-500)", textAlign: "center" }}>
                          +{dayEvents.length - 2}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Event type legend */}
            <div style={{ display: "flex", gap: 16, marginTop: 20, flexWrap: "wrap" }}>
              {EVENT_TYPES.map(t => (
                <div key={t.val} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 10, height: 10, borderRadius: "3px", background: t.color }} />
                  <span style={{ fontSize: "0.75rem", color: "var(--gray-500)" }}>{t.label}</span>
                </div>
              ))}
            </div>

            {/* Selected day events */}
            {selectedDay && (
              <div style={{ marginTop: 20, padding: "14px 16px", background: "var(--blue-100)", borderRadius: "var(--r8)" }}>
                <div style={{ fontWeight: 600, fontSize: "0.9rem", marginBottom: 10 }}>
                  {format(selectedDay, "EEEE, MMMM d")}
                </div>
                {selectedDayEvents.length === 0 ? (
                  <div style={{ fontSize: "0.84rem", color: "var(--gray-500)" }}>No events on this day</div>
                ) : (
                  selectedDayEvents.map(evt => (
                    <div key={evt.id} style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "8px 10px", background: "#fff",
                      borderRadius: "8px", marginBottom: 6,
                      borderLeft: `3px solid ${evt.color}`
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: "0.88rem" }}>{evt.title}</div>
                        {evt.description && <div style={{ fontSize: "0.78rem", color: "var(--gray-500)" }}>{evt.description}</div>}
                        <span style={{ fontSize: "0.7rem", background: evt.color + "20", color: evt.color, padding: "2px 8px", borderRadius: "10px", fontWeight: 600 }}>
                          {EVENT_TYPES.find(t => t.val === evt.type)?.label || evt.type}
                        </span>
                      </div>
                      {canManage && (
                        <button onClick={() => handleDelete(evt.id)} style={{
                          background: "none", border: "none", cursor: "pointer",
                          color: "var(--gray-500)", padding: 4, borderRadius: 4
                        }}
                        onMouseEnter={e => e.currentTarget.style.color = "var(--red-600)"}
                        onMouseLeave={e => e.currentTarget.style.color = "var(--gray-500)"}
                        >
                          <FiTrash2 size={13} />
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* Upcoming Events */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">🗓️ Upcoming Events</h3>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="loading-center" style={{ padding: 24 }}><div className="spinner" /></div>
              ) : upcomingEvents.length === 0 ? (
                <div className="empty-state" style={{ padding: "24px 0" }}>
                  <div className="empty-state-icon" style={{ fontSize: "2rem" }}>📅</div>
                  <div className="empty-state-title">No upcoming events</div>
                </div>
              ) : (
                upcomingEvents.map(evt => (
                  <div key={evt.id} style={{
                    display: "flex", gap: 12, alignItems: "flex-start",
                    padding: "12px 0",
                    borderBottom: "1px solid var(--border)"
                  }}>
                    <div style={{
                      width: 48, height: 48, borderRadius: "12px",
                      background: evt.color + "18",
                      display: "flex", flexDirection: "column",
                      alignItems: "center", justifyContent: "center",
                      flexShrink: 0
                    }}>
                      <div style={{ fontSize: "1rem", fontWeight: 800, color: evt.color, lineHeight: 1 }}>
                        {format(toDate(evt.date), "d")}
                      </div>
                      <div style={{ fontSize: "0.62rem", color: evt.color, textTransform: "uppercase", fontWeight: 600 }}>
                        {format(toDate(evt.date), "MMM")}
                      </div>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: "0.88rem", marginBottom: 3 }}>{evt.title}</div>
                      <span style={{
                        fontSize: "0.7rem", fontWeight: 600, padding: "2px 8px",
                        borderRadius: "10px", background: evt.color + "18", color: evt.color
                      }}>
                        {EVENT_TYPES.find(t => t.val === evt.type)?.label || "Event"}
                      </span>
                      {canManage && (
                        <button onClick={() => handleDelete(evt.id)} style={{
                          background: "none", border: "none", cursor: "pointer",
                          color: "var(--gray-500)", padding: "0 4px", float: "right"
                        }}
                        onMouseEnter={e => e.currentTarget.style.color = "var(--red-600)"}
                        onMouseLeave={e => e.currentTarget.style.color = "var(--gray-500)"}
                        >
                          <FiTrash2 size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Event Stats */}
          <div className="card">
            <div className="card-header"><h3 className="card-title">Event Summary</h3></div>
            <div className="card-body">
              {EVENT_TYPES.map(t => {
                const count = events.filter(e => e.type === t.val).length;
                return (
                  <div key={t.val} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 10, height: 10, borderRadius: "3px", background: t.color }} />
                      <span style={{ fontSize: "0.85rem" }}>{t.label}</span>
                    </div>
                    <span style={{ fontWeight: 700, fontSize: "0.9rem", color: t.color }}>{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Add Event Modal */}
      <Modal show={showModal} onClose={() => { setShowModal(false); setErrors({}); }} title="Add Calendar Event"
        footer={<>
          <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleAdd} disabled={saving}>
            {saving ? <><div className="spinner spinner-sm" /> Adding...</> : "Add Event"}
          </button>
        </>}
      >
        <div className="form-group">
          <label className="form-label">Event Title *</label>
          <input className={`form-control${errors.title ? " error" : ""}`} placeholder="e.g. Annual Sports Day"
            value={form.title} onChange={e => { setForm(p => ({ ...p, title: e.target.value })); setErrors(p => ({ ...p, title: "" })); }} />
          {errors.title && <div className="form-error">{errors.title}</div>}
        </div>
        <div className="g g2">
          <div className="form-group">
            <label className="form-label">Date *</label>
            <input type="date" className={`form-control${errors.date ? " error" : ""}`}
              value={form.date} onChange={e => { setForm(p => ({ ...p, date: e.target.value })); setErrors(p => ({ ...p, date: "" })); }} />
            {errors.date && <div className="form-error">{errors.date}</div>}
          </div>
          <div className="form-group">
            <label className="form-label">Event Type</label>
            <select className="form-control" value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
              {EVENT_TYPES.map(t => <option key={t.val} value={t.val}>{t.label}</option>)}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Description</label>
          <textarea className="form-control" rows={2} placeholder="Optional details..."
            value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
        </div>
      </Modal>

      <style>{`
        @media (max-width: 1024px) {
          div[style*="grid-template-columns: 1fr 300px"] { grid-template-columns: 1fr !important; }
        }
      `}</style>

    </div>
  );
}